/**
 * Utility functions for fetching website information from Google Places API
 */

import { getGoogleLocationDataWithAi } from '@/api/location';
import { warn } from '@/utils/logger';
import type { RawLocationData } from '@/utils/postTypes';

/**
 * Fetch website from Google Places API if missing from location data
 * 
 * @param locationData - Location data that may be missing website
 * @param locationName - Name of the location for search context
 * @param cityAddress - Optional city address for better search results
 * @returns Location data with website if found, or original data if not found/not a Google Place
 */
export async function fetchWebsiteIfMissing(
  locationData: RawLocationData | undefined | null,
  locationName?: string,
  cityAddress?: string
): Promise<RawLocationData | undefined | null> {
  if (!locationData) {
    return locationData;
  }

  // Check if website already exists
  const hasWebsite = !!locationData.website;
  
  if (hasWebsite) {
    return locationData;
  }

  // Only fetch for Google Places
  const googlePlaceId = locationData.google_place_id;
  if (!googlePlaceId) {
    return locationData;
  }

  // Need location name for search
  const searchName = locationName || locationData.name;
  if (!searchName) {
    warn('[websiteUtils] Cannot fetch website: missing location name', {
      googlePlaceId,
    });
    return locationData;
  }

  try {
    const freshLocations = await getGoogleLocationDataWithAi({
      title: searchName,
      body: cityAddress || '',
    });

    if (!freshLocations || freshLocations.length === 0) {
      return locationData;
    }

    // Find matching location by google_place_id if available, otherwise use first result
    let matchedLocation = freshLocations[0];
    if (googlePlaceId && freshLocations.length > 1) {
      const exactMatch = freshLocations.find(
        (loc) => loc.google_place_id === googlePlaceId
      );
      if (exactMatch) {
        matchedLocation = exactMatch;
      }
    }

    const fetchedWebsite = matchedLocation.website;
    
    if (fetchedWebsite) {
      return {
        ...locationData,
        website: fetchedWebsite,
      };
    }
    
    return locationData;
  } catch (error) {
    warn('[websiteUtils] Error fetching website from Google Places', {
      error: error instanceof Error ? error.message : String(error),
      locationName: searchName,
      googlePlaceId: googlePlaceId?.substring(0, 20),
    });
    return locationData;
  }
}

/** Websites looked up this session, by Google place id (null: none found). */
const websiteByPlaceId = new Map<string, string | null>();
const websiteLookups = new Set<string>();

/**
 * Save-path variant of fetchWebsiteIfMissing that never waits on the network. Each lookup
 * is an AI + Google search, and saves used to run one per website-less place, one after
 * another, before every POST — slow, and a reload in that window dropped the save. Returns
 * the location with a website already known; otherwise starts one background lookup per
 * place so a later save includes it.
 */
export function withKnownWebsite(
  locationData: RawLocationData | undefined | null,
  locationName?: string,
  cityAddress?: string
): RawLocationData | undefined | null {
  if (!locationData || locationData.website) return locationData;
  const placeId = locationData.google_place_id;
  if (!placeId) return locationData;

  const known = websiteByPlaceId.get(placeId);
  if (known) return { ...locationData, website: known };
  if (known === null || websiteLookups.has(placeId)) return locationData;

  websiteLookups.add(placeId);
  void fetchWebsiteIfMissing(locationData, locationName, cityAddress)
    .then((result) => {
      websiteByPlaceId.set(placeId, result?.website ?? null);
    })
    .finally(() => {
      websiteLookups.delete(placeId);
    });
  return locationData;
}
